import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from '@/hooks/useAuthState';
import AdminSidebar from '@/components/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Menu, LogOut, User, MessageCircle, Bell, Mail, Home } from 'lucide-react';
import { authService } from '@/services/authService';
import { useToast } from '@/hooks/use-toast';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getUserImageUrl } from '@/config/api';
import { useMessageNotifications } from '@/hooks/useMessageNotifications';
import { useNotifications } from '@/hooks/useNotifications';
import { useContactNotifications } from '@/hooks/useContactNotifications';
interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    userType,
    userName,
    userPhoto,
    userId,
    user,
    loading: authLoading,
  } = useAuthState();
  const { toast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { unreadCount, hasUnreadMessages, lastConversationId } = useMessageNotifications();
  const { unreadCount: notificationUnreadCount, hasUnreadNotifications } = useNotifications();
  const { unreadCount: contactUnreadCount, hasUnreadContacts } = useContactNotifications();

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated || userType !== 'admin') {
        navigate('/admin/login');
      }
    }
  }, [isAuthenticated, userType, authLoading, navigate]);

  const handleLogout = async () => {
    try {
      await authService.logout();
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso.",
      });
      navigate('/admin/login');
    } catch (error) {
      console.error('Erro no logout:', error);
    }
  };

  const handleMessageNotificationClick = () => {
    if (lastConversationId) {
      navigate(`/minha-area/mensagens?conversation=${lastConversationId}`);
    } else {
      navigate('/minha-area/mensagens');
    }
  };

  const userEmail = user?.email || '';

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || userType !== 'admin') {
    return null;
  }

  const userInitials = userName
    ? userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'AD';

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <AdminSidebar 
        isOpen={sidebarOpen} 
        isCollapsed={sidebarCollapsed}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <Menu className="h-5 w-5" />
              </Button>
              <h1 className="text-lg font-semibold">Área Administrativa</h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.location.href = '/'}
                className="gap-2"
                title="Voltar para o site"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Button>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              {/* Ícones de notificação */}
              <div className="flex items-center gap-1">
                {/* Ícone de notificação de mensagens */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleMessageNotificationClick}
                  className="relative hover:bg-primary/10 transition-colors"
                  title={hasUnreadMessages ? `${unreadCount} mensagens não lidas` : "Mensagens"}
                >
                  <MessageCircle className="h-5 w-5" />
                  {hasUnreadMessages && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>

                {/* Ícone de notificações gerais */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/minha-area/notificacoes')}
                  className="relative hover:bg-primary/10 transition-colors"
                  title={hasUnreadNotifications ? `${notificationUnreadCount} notificações não lidas` : "Notificações"}
                >
                  <Bell className="h-5 w-5" />
                  {hasUnreadNotifications && (
                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium animate-pulse">
                      {notificationUnreadCount > 9 ? '9+' : notificationUnreadCount}
                    </span>
                  )}
                </Button>

                {/* Ícone de contatos (admin) */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/admin/contatos')}
                  className="relative hover:bg-primary/10 transition-colors"
                  title={hasUnreadContacts ? `${contactUnreadCount} mensagens não lidas` : "Contatos"}
                >
                  <Mail className="h-5 w-5" />
                  {hasUnreadContacts && (
                    <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium animate-pulse">
                      {contactUnreadCount > 9 ? '9+' : contactUnreadCount}
                    </span>
                  )}
                </Button>
              </div>

              {/* Menu do usuário */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full md:hidden">
                    <Avatar className="h-10 w-10">
                      <AvatarImage 
                        src={userPhoto && userId ? getUserImageUrl(userId, userPhoto) : undefined} 
                        alt={userName || 'Admin'} 
                      />
                      <AvatarFallback>{userInitials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{userName || 'Administrador'}</p>
                      {userEmail && (
                        <p className="text-xs leading-none text-muted-foreground">
                          {userEmail}
                        </p>
                      )}
                      <p className="text-xs leading-none text-muted-foreground">
                        Administrador
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/minha-area/mensagens')}>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    <span>Mensagens</span>
                    {hasUnreadMessages && (
                      <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/minha-area/notificacoes')}>
                    <Bell className="mr-2 h-4 w-4" />
                    <span>Notificações</span>
                    {hasUnreadNotifications && (
                      <span className="ml-auto bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {notificationUnreadCount > 9 ? '9+' : notificationUnreadCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/contatos')}>
                    <Mail className="mr-2 h-4 w-4" />
                    <span>Contatos</span>
                    {hasUnreadContacts && (
                      <span className="ml-auto bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {contactUnreadCount > 9 ? '9+' : contactUnreadCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/minha-area/cadastro')}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Meu Perfil</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sair</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Menu do usuário (desktop) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full hidden md:flex">
                    <Avatar className="h-10 w-10">
                      <AvatarImage 
                        src={userPhoto && userId ? getUserImageUrl(userId, userPhoto) : undefined} 
                        alt={userName || 'Admin'} 
                      />
                      <AvatarFallback>{userInitials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{userName || 'Administrador'}</p>
                      {userEmail && (
                        <p className="text-xs leading-none text-muted-foreground">
                          {userEmail}
                        </p>
                      )}
                      <p className="text-xs leading-none text-muted-foreground">
                        Administrador
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/minha-area/mensagens')}>
                    <MessageCircle className="mr-2 h-4 w-4" />
                    <span>Mensagens</span>
                    {hasUnreadMessages && (
                      <span className="ml-auto bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/minha-area/notificacoes')}>
                    <Bell className="mr-2 h-4 w-4" />
                    <span>Notificações</span>
                    {hasUnreadNotifications && (
                      <span className="ml-auto bg-blue-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {notificationUnreadCount > 9 ? '9+' : notificationUnreadCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/admin/contatos')}>
                    <Mail className="mr-2 h-4 w-4" />
                    <span>Contatos</span>
                    {hasUnreadContacts && (
                      <span className="ml-auto bg-orange-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {contactUnreadCount > 9 ? '9+' : contactUnreadCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/minha-area/cadastro')}>
                    <User className="mr-2 h-4 w-4" />
                    <span>Meu Perfil</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sair</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-background">
          <div className="h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;

